import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { billingApi } from '../../../services/billingApi';
import { currentUserHasCerebralAdminRole } from '../../../utils/roleCheck';
import { Alert, Button, Select, TextInput } from '../../../components/common';
import { SectionCard, StatusBadge, EmptyState } from './components';
import { formatCurrency, formatDateOnly } from './format';
import './BillingAdmin.css';

const STATUS_FILTERS = [
  { id: '', label: 'All' },
  { id: 'draft', label: 'Draft' },
  { id: 'finalized', label: 'Finalized' },
  { id: 'paid', label: 'Paid' },
  { id: 'void', label: 'Void' },
];

export default function InvoiceListPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [accountFilter, setAccountFilter] = useState('');

  useEffect(() => {
    if (!currentUserHasCerebralAdminRole()) navigate('/user-home');
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await billingApi.listInvoices({ pageSize: 100 });
      setInvoices(data?.invoices || data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const accountOptions = useMemo(() => {
    const set = new Map();
    invoices.forEach((i) => {
      const name = i.AccountName || i.account_name;
      if (name) set.set(name, name);
    });
    return [...set.values()].sort().map((v) => ({ value: v, label: v }));
  }, [invoices]);

  const filtered = useMemo(() => {
    return invoices.filter((i) => {
      const status = (i.Status || i.status || 'draft').toLowerCase();
      if (statusFilter && status !== statusFilter) return false;
      const account = i.AccountName || i.account_name || '';
      if (accountFilter && account !== accountFilter) return false;
      const periodStart = i.PeriodStart || i.period_start;
      const periodEnd = i.PeriodEnd || i.period_end;
      if (startDate && periodStart && new Date(periodStart) < new Date(startDate)) return false;
      if (endDate && periodEnd && new Date(periodEnd) > new Date(`${endDate}T23:59:59`)) return false;
      return true;
    });
  }, [invoices, statusFilter, startDate, endDate, accountFilter]);

  const clearFilters = () => {
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
    setAccountFilter('');
  };
  const hasActiveFilters = !!(statusFilter || startDate || endDate || accountFilter);

  return (
    <div className="user-home-layout__page">
      <Link to="/user-home/admin/billing" className="billing-admin__breadcrumb">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
        Back to dashboard
      </Link>

      <div className="billing-admin__page-header">
        <div className="billing-admin__header-text">
          <h1 className="user-home-layout__page-title">Invoices</h1>
          <p className="user-home-layout__page-subtitle">
            Generated invoices for every CompanyAccount × billing period. Click any row to drill into
            line items, or download the CSV for your accounting system.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="error" title="Could not load invoices" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <SectionCard
        title="Filters"
        toolbar={hasActiveFilters ? <Button variant="ghost" onClick={clearFilters}>Clear filters</Button> : null}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
          <div>
            <p className="billing-admin__filters-label" style={{ margin: '0 0 6px' }}>Status</p>
            <div className="billing-admin__segmented" role="tablist" aria-label="Status filter">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id || 'all'}
                  type="button"
                  className={`billing-admin__segmented-btn ${statusFilter === f.id ? 'is-active' : ''}`}
                  onClick={() => setStatusFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <TextInput
            variant="modal"
            label="Period from"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextInput
            variant="modal"
            label="Period to"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <Select
            variant="modal"
            label="Account"
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            options={accountOptions}
            placeholder="All accounts"
          />
        </div>
      </SectionCard>

      <SectionCard
        title={`Invoices ${!loading ? `(${filtered.length})` : ''}`}
        flush
      >
        <div className="billing-admin__table-wrap">
          <table className="billing-admin__table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Account</th>
                <th>Period</th>
                <th>Status</th>
                <th className="num">Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 5 }).map((_, idx) => (
                <tr key={`sk-${idx}`} className="billing-admin__skeleton-row">
                  {Array.from({ length: 6 }).map((__, c) => (
                    <td key={c}><span className="billing-admin__skeleton" /></td>
                  ))}
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 0, border: 0 }}>
                    <EmptyState
                      icon={
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                        </svg>
                      }
                      title={hasActiveFilters ? 'No invoices match those filters' : 'No invoices yet'}
                      message={
                        hasActiveFilters
                          ? 'Try widening the date range, or clear the status filter.'
                          : 'Once a billing period closes, invoices will appear here.'
                      }
                      action={hasActiveFilters ? <Button variant="ghost" onClick={clearFilters}>Clear filters</Button> : null}
                    />
                  </td>
                </tr>
              )}
              {!loading && filtered.map((i) => {
                const id = i.InvoiceId || i.invoice_id;
                const total = i.TotalCents ?? i.total_cents ?? 0;
                const currency = i.Currency || i.currency || 'USD';
                const status = (i.Status || i.status || 'draft').toLowerCase();
                const periodStart = i.PeriodStart || i.period_start;
                const periodEnd = i.PeriodEnd || i.period_end;
                return (
                  <tr key={id}>
                    <td className="billing-admin__table-cell-primary">
                      <Link className="billing-admin__table-row-link" to={`/user-home/admin/billing/invoices/${id}`}>
                        {i.InvoiceNumber || i.invoice_number || id}
                      </Link>
                    </td>
                    <td>{i.AccountName || i.account_name || <span className="billing-admin__muted">—</span>}</td>
                    <td>
                      <span>{formatDateOnly(periodStart)}</span>
                      <span className="billing-admin__muted"> → </span>
                      <span>{formatDateOnly(periodEnd)}</span>
                    </td>
                    <td><StatusBadge status={status} /></td>
                    <td className="num"><strong>{formatCurrency(total, currency)}</strong></td>
                    <td className="num">
                      <a href={billingApi.invoiceCsvUrl(id)} download style={{ textDecoration: 'none' }}>
                        <Button variant="ghost">CSV</Button>
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
